/**
 * Map config — tokens/styles only. Never OEM/telematics secrets.
 * ADR: docs/pmv2/wave2/MAP-FRONTEND-ADR.md
 */

import type { StyleSpecification } from 'maplibre-gl';

/** Free OSM raster style (no token). Prefer VITE_PMV_MAP_STYLE_URL when set. */
export const OSM_RASTER_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
      maxzoom: 19,
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
};

export function getFleetMapStyleUrl(satellite = false): string | StyleSpecification {
  if (satellite) {
    const sat = import.meta.env.VITE_PMV_MAP_SATELLITE_STYLE_URL as string | undefined;
    if (sat?.trim()) return sat.trim();
  }
  const primary = import.meta.env.VITE_PMV_MAP_STYLE_URL as string | undefined;
  if (primary?.trim()) return primary.trim();
  return OSM_RASTER_STYLE;
}

export type FleetMapBBox = {
  min_lat: number;
  max_lat: number;
  min_lon: number;
  max_lon: number;
};

export type FleetMapMarkerInput = {
  id: string;
  lat: number;
  lon: number;
  label?: string;
  freshness?: string | null;
  connectivity?: string | null;
  movement?: string | null;
  hasAttention?: boolean;
};

export type FleetMapGeofenceInput = {
  id: string;
  name?: string | null;
  type?: string | null;
  geometry_type: string;
  center_lat?: number | null;
  center_lon?: number | null;
  radius_m?: number | null;
  geometry_json?: { type?: string; coordinates?: number[][][] } | null;
};

/** Approximate circle as GeoJSON polygon (for overlay). */
export function circleToPolygon(
  lat: number,
  lon: number,
  radiusM: number,
  steps = 48
): GeoJSON.Feature<GeoJSON.Polygon> {
  const coords: number[][] = [];
  const R = 6371000;
  for (let i = 0; i <= steps; i++) {
    const bearing = (i / steps) * 2 * Math.PI;
    const lat1 = (lat * Math.PI) / 180;
    const lon1 = (lon * Math.PI) / 180;
    const ang = radiusM / R;
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(ang) + Math.cos(lat1) * Math.sin(ang) * Math.cos(bearing)
    );
    const lon2 =
      lon1 +
      Math.atan2(
        Math.sin(bearing) * Math.sin(ang) * Math.cos(lat1),
        Math.cos(ang) - Math.sin(lat1) * Math.sin(lat2)
      );
    coords.push([(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [coords] },
  };
}
